import { FormEvent, useCallback, useId, useRef, useState } from "react";
import { FileUp, X } from "lucide-react";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { useNotification } from "../Notification/NotificationProvider";
import { createPolicyCheck } from "../../api/policyCheck";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import {
  formatPolicyCheckFileSize,
  isAcceptedPolicyCheckFile,
  POLICY_CHECK_FILE_ACCEPT,
  POLICY_CHECK_MAX_FILE_BYTES,
} from "../../utils/policyCheckFile";
import "../CreateRequestModal/CreateRequestModal.scss";
import "./CreatePolicyCheckModal.scss";

interface CreatePolicyCheckModalProps {
  onClose: () => void;
  onCreated: (payload: { fileId: string; companyName: string; inn: string }) => void;
}

export const CreatePolicyCheckModal = ({ onClose, onCreated }: CreatePolicyCheckModalProps) => {
  const { showNotification } = useNotification();
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [companyName, setCompanyName] = useState("");
  const [inn, setInn] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [companyNameError, setCompanyNameError] = useState<string | null>(null);
  const [innError, setInnError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const clearFile = useCallback(() => {
    setFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      let hasError = false;
      setCompanyNameError(null);
      setInnError(null);
      setFileError(null);
      setGeneralError(null);

      if (!companyName.trim()) {
        setCompanyNameError("Укажите название компании");
        hasError = true;
      }

      if (!inn.trim()) {
        setInnError("Укажите ИНН");
        hasError = true;
      }

      if (!file) {
        setFileError("Выберите файл политики");
        hasError = true;
      } else if (!isAcceptedPolicyCheckFile(file)) {
        setFileError("Допустимы файлы PDF, DOC или DOCX");
        hasError = true;
      } else if (file.size > POLICY_CHECK_MAX_FILE_BYTES) {
        setFileError("Размер файла не должен превышать 2 МБ");
        hasError = true;
      }

      if (hasError || !file) return;

      setSubmitting(true);
      try {
        const created = await createPolicyCheck({
          companyName: companyName.trim(),
          inn: inn.trim(),
          file,
        });
        const fileId = created.id?.trim();
        if (!fileId) {
          throw new Error("Сервер не вернул идентификатор проверки");
        }
        showNotification({ message: "Проверка политики создана", variant: "success" });
        onCreated({
          fileId,
          companyName: companyName.trim(),
          inn: inn.trim(),
        });
      } catch (err: unknown) {
        setGeneralError(getApiErrorMessage(err, "Не удалось создать проверку политики"));
      } finally {
        setSubmitting(false);
      }
    },
    [companyName, file, inn, onCreated, showNotification]
  );

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div className="detail-checks-modal" onClick={(e) => e.stopPropagation()}>
          <div className="detail-checks-modal-head">
            <h3>Проверка политики обработки ПД</h3>
            <button type="button" className="detail-tasklist-close" onClick={onClose} aria-label="Закрыть">
              <X size={18} />
            </button>
          </div>

          <form
            id="create-policy-check-form"
            className="detail-checks-modal-body create-request-modal-form"
            onSubmit={handleSubmit}
          >
            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label" htmlFor="create-policy-check-company">
                Название компании
              </label>
              <input
                id="create-policy-check-company"
                type="text"
                className="create-request-modal-input"
                value={companyName}
                autoFocus
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setCompanyNameError(null);
                  setGeneralError(null);
                }}
              />
              {companyNameError ? (
                <span className="create-request-modal-field-error">{companyNameError}</span>
              ) : null}
            </div>

            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label" htmlFor="create-policy-check-inn">
                ИНН
              </label>
              <input
                id="create-policy-check-inn"
                type="text"
                className="create-request-modal-input"
                value={inn}
                onChange={(e) => {
                  setInn(e.target.value);
                  setInnError(null);
                  setGeneralError(null);
                }}
              />
              {innError ? <span className="create-request-modal-field-error">{innError}</span> : null}
            </div>

            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label" htmlFor={fileInputId}>
                Файл политики
              </label>
              <input
                id={fileInputId}
                ref={fileInputRef}
                type="file"
                accept={POLICY_CHECK_FILE_ACCEPT}
                className="create-policy-check-file-input"
                onChange={(e) => {
                  const nextFile = e.target.files?.[0] ?? null;
                  setFile(nextFile);
                  setFileError(null);
                  setGeneralError(null);
                }}
              />
              <label
                htmlFor={fileInputId}
                className={`create-policy-check-file-btn${file ? " create-policy-check-file-btn--filled" : ""}${
                  fileError ? " create-policy-check-file-btn--error" : ""
                }`}
              >
                <FileUp size={18} className="create-policy-check-file-btn__icon" aria-hidden />
                <span className="create-policy-check-file-btn__text">
                  {file ? file.name : "Загрузить файл (PDF, DOC, DOCX, до 2 МБ)"}
                </span>
                {file ? (
                  <span className="create-policy-check-file-btn__meta">
                    {formatPolicyCheckFileSize(file.size)}
                  </span>
                ) : null}
              </label>
              {file ? (
                <button type="button" className="create-policy-check-file-clear" onClick={clearFile}>
                  Удалить файл
                </button>
              ) : null}
              {fileError ? <span className="create-request-modal-field-error">{fileError}</span> : null}
            </div>

            {generalError ? (
              <span className="create-request-modal-general-error">{generalError}</span>
            ) : null}
          </form>

          <div className="detail-checks-modal-footer">
            <button
              type="submit"
              form="create-policy-check-form"
              className="create-request-modal-submit"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size="sm" /> : "Запустить проверку"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
